import * as React from 'react';

/**
 * Custom hook to measure the actual width of a component's container
 * @param shouldMeasure - Whether to actively measure the container width
 * @param fallbackWidth - Fallback width to use when not measuring
 * @returns { ref, width } - ref to attach to element, measured width
 */
export const useContainerWidth = <T extends HTMLElement | SVGSVGElement>(
  shouldMeasure: boolean = false,
  fallbackWidth: number = 180
) => {
  const [containerWidth, setContainerWidth] = React.useState(fallbackWidth);
  const elementRef = React.useRef<T>(null);

  React.useEffect(() => {
    if (!shouldMeasure || !elementRef.current) return undefined;

    const updateWidth = () => {
      const parent = elementRef.current?.parentElement;
      if (parent) {
        setContainerWidth(parent.getBoundingClientRect().width);
      }
    };

    updateWidth();
    window.addEventListener('resize', updateWidth);

    return () => window.removeEventListener('resize', updateWidth);
  }, [shouldMeasure]);

  return {
    ref: elementRef,
    width: containerWidth,
  };
};
